/**
 * <s-free-ship> — free-shipping progress bar that follows the live cart.
 *
 * Liquid renders the first state from `cart`; this refreshes it from the cart JSON
 * whenever the cart drawer opens (fired after every add-to-cart) or the page is restored from cache.
 *
 * Attributes:
 *   data-cart-url       — routes.cart_url from Liquid (".js" is appended for the JSON endpoint)
 *   data-threshold      — free-shipping threshold in the shop currency, in cents
 *   data-remaining-text — translated message with __AMOUNT__ where the remaining amount goes
 *   data-reached-text   — translated message once the threshold is met
 */
class SFreeShip extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    document.addEventListener('s-cart:opened', () => this.refresh(), { signal });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.refresh();
    }, { signal });
    this.refresh();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  async refresh() {
    const cartUrl = this.dataset.cartUrl;
    if (!cartUrl) return;
    try {
      // The .js suffix is the Ajax API contract for a JSON cart response.
      const response = await fetch(`${cartUrl}.js`, {
        headers: { Accept: 'application/json' },
        signal: this.controller?.signal,
      });
      if (!response.ok) return;
      const cart = await response.json();
      this.render(cart.total_price, cart.currency);
    } catch {
      // Keep the server-rendered state if the cart can't be read.
    }
  }

  render(total, currency) {
    // The threshold is set in the shop currency; convert it for shoppers browsing in another currency.
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const threshold = Math.round(Number(this.dataset.threshold) * rate);
    if (!threshold) return;

    const remaining = Math.max(threshold - total, 0);
    const percent = Math.min(100, Math.round((total / threshold) * 100));
    const reached = remaining === 0;

    const fill = this.querySelector('[data-fs-fill]');
    const bar = this.querySelector('[role="progressbar"]');
    const message = this.querySelector('[data-fs-message]');

    if (fill) fill.style.width = `${percent}%`;
    if (bar) bar.setAttribute('aria-valuenow', String(percent));
    this.toggleAttribute('data-reached', reached);

    if (!message) return;
    if (reached) {
      message.textContent = this.dataset.reachedText || '';
      return;
    }
    const locale = document.documentElement.lang || 'en-AU';
    let amount;
    try {
      amount = new Intl.NumberFormat(locale, { style: 'currency', currency }).format(remaining / 100);
    } catch {
      amount = (remaining / 100).toFixed(2);
    }
    message.textContent = (this.dataset.remainingText || '').replace('__AMOUNT__', amount);
  }
}

if (!customElements.get('s-free-ship')) {
  customElements.define('s-free-ship', SFreeShip);
}
